import { fieldWriter } from "./loanApi";
import elliemaeApi from "./interceptors/elliemaeApi";
import { LOAN_PROGRAM_CODES } from "../config/organizations";
import { isLoanProgram } from "../utils/loanProgramUtil";
/**
 * Get Borrower Pairs for a Loan
 * @param {string} accessToken
 * @param {string} loanId
 * @returns {Promise<Object|null>}
 */
export const getBorrowerPairs = async (accessToken, loanId) => {
    const url = `/encompass/v3/loans/${loanId}/applications`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching borrower pairs:", error.message);
        throw error;
    }
};

/**
 * Add Borrower Pair
 * @param {string} accessToken
 * @param {string} loanId
 * @param {Object} payload
 * @returns {Promise<Object|null>}
 */
export const addBorrowerPair = async (accessToken, loanId, payload) => {
    const url = `/encompass/v1/loans/${loanId}/applications`;

    try {
        const response = await elliemaeApi.post(url, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error adding borrower pair:", error.message);
        throw error;
    }
};

/**
 * Update Borrower Pair
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} borrowerPairId
 * @param {Object} payload
 * @returns {Promise<Object|null>}
 */
export const updateBorrowerPair = async (accessToken, loanId, borrowerPairId, payload) => {
    const url = `/encompass/v1/loans/${loanId}/applications/${borrowerPairId}`;

    try {
        const response = await elliemaeApi.patch(url, payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error updating borrower pair:", error.message);
        throw error;
    }
};

/**
 * Delete Borrower Pair
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} borrowerPairId
 * @returns {Promise<void>}
 */
export const deleteBorrowerPair = async (accessToken, loanId, borrowerPairId) => {
    const url = `/encompass/v1/loans/${loanId}/applications/${borrowerPairId}`;

    try {
        await elliemaeApi.delete(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });
    } catch (error) {
        console.error("Error deleting borrower pair:", error.message);
        throw error;
    }
};


/**
 * Delete all employments for an application (borrower or coborrower)
 */
export const deleteEmploymentsForApplication = async (
    accessToken,
    loanId,
    applicationId,
    isCoBorrower = false
) => {
    const base = `/encompass/v3/loans/${loanId}/applications/${applicationId}`;
    const role = isCoBorrower ? "coborrower" : "borrower";

    // 1. Get employment list
    const getUrl = `${base}/${role}/employment`;
    const getResp = await elliemaeApi.get(getUrl, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    const ids = (getResp.data || []).map(e => e.id || e.altId).filter(Boolean);
    if (ids.length === 0) return true;

    // 2. Patch delete
    const deleteUrl = `${base}/${role}/employment?action=delete&view=entity`;
    await elliemaeApi.patch(
        deleteUrl,
        ids.map(id => ({ id })),
        { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return true;
};

/**
 * Delete all other income sources for an application
 */
export const deleteOtherIncomeSourcesForApplication = async (
    accessToken,
    loanId,
    applicationId
) => {
    const base = `/encompass/v3/loans/${loanId}/applications/${applicationId}`;

    // 1. Get other incomes
    const getUrl = `${base}/otherIncomeSources`;
    const getResp = await elliemaeApi.get(getUrl, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    const ids = (getResp.data || []).map(o => o.id || o.altId).filter(Boolean);
    if (ids.length === 0) return true;

    // 2. Patch delete
    const deleteUrl = `${base}/otherIncomeSources?action=delete&view=entity`;
    await elliemaeApi.patch(
        deleteUrl,
        ids.map(id => ({ id })),
        { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return true;
};

/**
 * Remove all incomes (employment + other income) for all applications in a loan
 */
export const removeAllIncomes = async (accessToken, loanId) => {
    try {
        const apps = await getBorrowerPairs(accessToken, loanId);
        const appIds = (apps || []).map(a => a.id).filter(Boolean);
        const results = [];
        let appIndex = 1;
        for (const appId of appIds) {
            const r1 = await deleteEmploymentsForApplication(accessToken, loanId, appId, false);
            const r2 = await deleteEmploymentsForApplication(accessToken, loanId, appId, true);
            const r3 = await deleteOtherIncomeSourcesForApplication(accessToken, loanId, appId);

            // clear fields for this application index
            const r4 = await clearIncomeRelatedFieldsForApplication(
                accessToken,
                loanId,
                appIndex
            );

            const r5 = await clearRentalIncomeForApplication(accessToken, loanId, appId);

            results.push({
                appId,
                borrowerEmployment: r1,
                coborrowerEmployment: r2,
                otherIncome: r3,
                clearedFields: r4,
                rentalCleared: r5
            });

            appIndex++;
        }

        return { ok: true, results };
    } catch (err) {
        console.error("removeAllIncomes failed", err?.response?.data || err.message || err);
        return { ok: false, error: err };
    }
};

/**
 * Determine whether scenario matches (case-insensitive)
 * returns boolean
 */
export const matchesNonIncomeScenario = (brokerVal = "", docTypeVal = "", incomeTypeVal = "") => {
    const d = (docTypeVal || "").toUpperCase().trim();
    const i = (incomeTypeVal || "").toUpperCase().trim();

    // ACCELERATOR / BREEZY
    const isScenario1 =
        isLoanProgram(brokerVal, LOAN_PROGRAM_CODES.PROGRAM_002) && d === "DSCR" && i === "DSCR";

    // ALLY - ChampsTPO only, so this scenario never matches on SkyTPO.
    const isScenario2 =
        isLoanProgram(brokerVal, LOAN_PROGRAM_CODES.PROGRAM_001) &&
        d === "NONE" &&
        i === "NO INCOME/ NO EMPLOYMENT".toUpperCase();

    return isScenario1 || isScenario2;
};

/**
 * Evaluate updatedFields and, if watched fields changed, use currentValues (if provided)
 * to decide whether to delete incomes. Falls back to fieldReader(fetch) if currentValues
 * not provided.
 *
 * @param {string} accessToken
 * @param {string} loanId
 * @param {Array<{id:string, value:any}>} updatedFields - payload you plan to write (IDs may include "#1" suffix)
 * @param {Object} [currentValues] - optional map of current UI values (e.g. fieldValues from Index.js)
 * @returns {Promise<{triggered:boolean, matched:boolean, deleteResult?:object, values?:object, error?:any}>}
 */
export const evaluateAndRemoveIncomes = async (accessToken, loanId, updatedFields = [], currentValues = undefined) => {
    try {
        if (!accessToken || !loanId) {
            return { triggered: false, matched: false, error: "invalid parameters" };
        }

        const watchedIds = ["CX.LOAN.PROD.BROKER.SELECT", "CX.DOC.TYPE", "CX.INCOME.TYPE"];

        // Normalize updated field ids (strip #index and .P# suffix)
        const normalizedUpdatedIds = (updatedFields || []).map(f => {
            let id = (f.id || "").toString();
            id = id.split("#")[0];                // remove #index
            id = id.replace(/\.P[2-6]$/, "");     // remove .P2..P6 suffixes
            return id;
        });

        const hasWatchedChange = watchedIds.some(w => normalizedUpdatedIds.includes(w));
        if (!hasWatchedChange) {
            return { triggered: false, matched: false };
        }

        let brokerVal = "";
        let docTypeVal = "";
        let incomeTypeVal = "";

        brokerVal = (currentValues["CX.LOAN.PROD.BROKER.SELECT"] || "").toString();
        docTypeVal = (currentValues["CX.DOC.TYPE"] || "").toString();
        incomeTypeVal = (currentValues["CX.INCOME.TYPE"] || "").toString();

        const matched = matchesNonIncomeScenario(brokerVal, docTypeVal, incomeTypeVal);

        if (!matched) {
            return { triggered: true, matched: false, values: { brokerVal, docTypeVal, incomeTypeVal } };
        }

        // matched -> perform deletion
        const deleteResult = await removeAllIncomes(accessToken, loanId);
        return { triggered: true, matched: true, deleteResult, values: { brokerVal, docTypeVal, incomeTypeVal } };
    } catch (err) {
        console.error("evaluateAndRemoveIncomes error", err?.response?.data || err.message || err);
        return { triggered: true, matched: false, error: err };
    }
};

// List of borrower income fields to clear
const CLEAR_FIELDS = [
    "101", "102", "103", "104", "105", "106", "107",
    "1815", "1389",
    "110", "111", "112", "113", "114", "115", "116",
    "1816"
];

/**
 * Clears income-related fields for a given application index
 * Example:
 *   appIndex = 1 → writes 101#1, 102#1...
 *   appIndex = 2 → writes 101#2, 102#2...
 */
const clearIncomeRelatedFieldsForApplication = async (accessToken, loanId, appIndex) => {
    const body = CLEAR_FIELDS.map(id => ({
        id: `${id}#${appIndex}`,
        value: ""               // clear field
    }));

    try {
        await fieldWriter(accessToken, loanId, body);
        return { ok: true };
    } catch (err) {
        console.error("Failed clearing income fields", err?.response?.data || err.message);
        return { ok: false, error: err };
    }
};

/**
 * Get all REO properties for an application (borrower or co-borrower)
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} applicationId
 * @returns {Promise<Array>}
 */
export const getReoPropertiesForApplication = async (
    accessToken,
    loanId,
    applicationId
) => {
    const url = `/encompass/v3/loans/${loanId}/applications/${applicationId}/reoProperties`;

    try {
        const resp = await elliemaeApi.get(url, {
            headers: { Authorization: `Bearer ${accessToken}` }
        });
        return resp.data || [];
    } catch (err) {
        console.error("Error fetching REO properties:", err?.response?.data || err.message);
        throw err;
    }
};


/**
 * Clear rental income (set rentalIncomeNetAmount = 0) for all REO properties
 * where net rental income > 0
 *
 * @param {string} accessToken
 * @param {string} loanId
 * @param {string} applicationId
 * @returns {Promise<{ok:boolean, updated?:number, error?:any}>}
 */
export const clearRentalIncomeForApplication = async (
    accessToken,
    loanId,
    applicationId
) => {
    try {
        const list = await getReoPropertiesForApplication(accessToken, loanId, applicationId);

        // Filter only REOs with rental income > 0
        const toUpdate = list
            .filter(r => (r.rentalIncomeNetAmount || 0) > 0)
            .map(r => ({
                id: r.id || r.altId,
                rentalIncomeNetAmount: 0,
                participationPercentage: 0,
                percentageofRental: 0,
                maintenanceExpenseAmount: "",
                rentalIncomeGrossAmount: 0
            }));

        if (toUpdate.length === 0) {
            return { ok: true, updated: 0 };
        }

        const patchUrl = `/encompass/v3/loans/${loanId}/applications/${applicationId}/reoProperties?action=update&view=entity`;

        await elliemaeApi.patch(
            patchUrl,
            toUpdate,
            { headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" } }
        );

        return { ok: true, updated: toUpdate.length };
    } catch (err) {
        console.error("Error clearing rental income:", err?.response?.data || err.message);
        return { ok: false, error: err };
    }
};
